import React from 'react';
import { Link } from 'react-router-dom';

/**
 * The button treatments in the design, measured off the Figma nodes rather
 * than eyeballed:
 *
 *   primary — the flat brand #2563eb (bg/primary), no shadow. The
 *             repaint dropped the gradient the orange design used here.
 *   dark    — neutral/900 #101828. Note this is *not* ground/800, which the
 *             design reserves for the dark card.
 *   muted   — the dark fill with a dimmed label, "view details" beside
 *             "subscribe" on a catalogue card.
 *
 * Two radii, both from the design: 12px on the standard button, 14px on the
 * small one in the header.
 *
 * Renders as <Link> when given `to`, <a> when given `href`, <button> otherwise,
 * so callers never have to pick the element that carries the styling.
 */

// The `focus:`/`active:` label colours repeat the resting one on purpose.
// public/css/rsuite.min.css is loaded globally and carries `a:focus, a:hover`
// and `a:active` colour rules; those score (0,1,1) and a bare Tailwind colour
// utility scores (0,1,0), so a button rendered as <Link> or <a> flashes rsuite
// blue the moment it is clicked. Restating the colour under a variant lifts it
// to (0,2,0) and it wins again. Harmless on the <button> form, which those
// rules never matched.
const VARIANTS = {
    primary: 'tw-bg-v2-brand tw-text-white focus:tw-text-white active:tw-text-white hover:tw-brightness-105',
    dark: 'tw-bg-v2-neutral-900 tw-text-white focus:tw-text-white active:tw-text-white hover:tw-bg-black',
    // `muted` brightens its label on hover; focus takes the same brightened
    // value rather than the resting one, because Tailwind emits `focus:` after
    // `hover:` and the resting value would otherwise win back on a hovered
    // button that also holds focus.
    muted: 'tw-bg-v2-neutral-900 tw-text-white/60 hover:tw-text-white/85 focus:tw-text-white/85 active:tw-text-white/85',
    // "Previous" on the sign-up wizard: white, drawn and lettered in gray/550.
    // The only outlined button the design has.
    outline: 'tw-border tw-border-solid tw-border-v2-neutral-550 tw-bg-white tw-text-v2-neutral-550 focus:tw-text-v2-neutral-550 active:tw-text-v2-neutral-550 hover:tw-bg-v2-neutral-50',
};

// Three sizes, one per button in the design. They differ in radius and weight
// as well as scale, which is why the radius lives here rather than in the base.
const SIZES = {
    // Header sign-in: 40px tall, 20px sides, 14px regular, 14px radius.
    sm: 'tw-h-10 tw-px-5 tw-text-v2-sm tw-rounded-v2-btn-sm tw-font-normal',
    // Call to action pair: 48px tall, 28px sides, 14px bold, 12px radius.
    md: 'tw-h-12 tw-px-7 tw-text-v2-sm tw-rounded-v2-btn tw-font-bold',
    // Hero: 48px tall, 28px sides, 16px regular, 14px radius.
    lg: 'tw-h-12 tw-px-7 tw-text-v2-base tw-rounded-v2-btn-sm tw-font-normal',
    // The contact page's two submits: 44px tall, 24px sides, 14px regular,
    // 12px radius. A size of its own rather than `md` plus a height class from
    // the caller — two `h-*` utilities in one attribute are settled by the
    // stylesheet's order, so an override written that way holds only by luck.
    form: 'tw-h-11 tw-px-6 tw-text-v2-sm tw-rounded-v2-btn tw-font-normal',
    // The pair on a catalogue card, which the design draws a size down from
    // the rest: 43px tall, 20px sides, 13px bold, 12px radius. A size rather
    // than classes passed in by the caller — two utilities of the same kind
    // are settled by the stylesheet's order, not the attribute's.
    compact: 'tw-h-[43px] tw-px-[20px] tw-text-[13px] tw-rounded-v2-btn tw-font-bold',
    // The auth cards' one button: 48px tall, 24px sides, 16px bold, 10px
    // radius. It always fills its card, but the width stays with the caller —
    // a `w-*` inside a size would be settled against a caller's own `w-*` by
    // the stylesheet's order, which is the trap the two notes above describe.
    //
    // 10px rather than 8: the current canvas draws this button at the file's
    // "Border Raduis 2" on sign-in, verification, the first wizard step and
    // "check your email", and at `radius/lg` on the other two steps. Four
    // against two.
    auth: 'tw-h-[48px] tw-px-6 tw-text-[16px] tw-rounded-v2-field tw-font-bold',
};

const Button = ({
    variant = 'primary',
    size = 'md',
    to,
    href,
    icon,
    className = '',
    children,
    ...rest
}) => {
    const classes = [
        // `gap` handles icon spacing, so the icon needs no margin of its own
        // and stays on the correct side when the page flips to English.
        'tw-inline-flex tw-items-center tw-justify-center tw-gap-2',
        'tw-whitespace-nowrap tw-leading-[1.6]',
        'tw-transition tw-duration-200',
        'focus-visible:tw-outline-none focus-visible:tw-ring-2',
        'focus-visible:tw-ring-v2-brand focus-visible:tw-ring-offset-2',
        'disabled:tw-opacity-50 disabled:tw-cursor-not-allowed',
        VARIANTS[variant] ?? VARIANTS.primary,
        SIZES[size] ?? SIZES.md,
        className,
    ].join(' ');

    const content = (
        <>
            {children}
            {icon}
        </>
    );

    if (to) return <Link to={to} className={classes} {...rest}>{content}</Link>;

    if (href) {
        // Anything leaving the site opens in a new tab; `noreferrer` keeps the
        // target from reaching back through window.opener. An href that stays
        // on the page — `#curricula` under the catalogue hero — must not, or
        // jumping down one section opens a second copy of the site.
        const leavesTheSite = !href.startsWith('#') && !href.startsWith('/');

        return (
            <a
                href={href}
                target={leavesTheSite ? '_blank' : undefined}
                rel={leavesTheSite ? 'noopener noreferrer' : undefined}
                className={classes}
                {...rest}
            >
                {content}
            </a>
        );
    }

    return <button type="button" className={classes} {...rest}>{content}</button>;
};

export default Button;
